/** @format */

"use client";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { signIn } from "next-auth/react";
import { useState, useEffect, useRef } from "react";
import { useParams } from "next/navigation";

export const AuthButton = () => {
  const [isSigningIn, setIsSigningIn] = useState(false);
  const params = useParams();
  const lng = params?.lng as string || 'en';
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleSignIn = async () => {
    try {
      setIsSigningIn(true);

      // Safety timeout: reset loading state after 30 seconds
      // In case redirect fails or gets blocked
      timeoutRef.current = setTimeout(() => {
        console.warn('Sign in timeout - resetting loading state');
        setIsSigningIn(false);
      }, 30000);

      const result = await signIn("google", {
        callbackUrl: `/${lng}`,
        redirect: true,
      });

      // Clear timeout if signIn completes
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // If signIn returns without redirecting (shouldn't happen with redirect: true)
      // but just in case, reset the state
      if (result?.error) {
        console.error('Sign in error:', result.error);
        setIsSigningIn(false);
      }
    } catch (error) {
      console.error('Sign in failed:', error);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setIsSigningIn(false);
    }
  };

  const GoogleIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );

  return (
    <div className="flex flex-col gap-4 w-full mt-4">
      <Button
        onClick={handleSignIn}
        disabled={isSigningIn}
        className="w-full bg-[#4285F4] hover:bg-[#357ae8]"
        aria-label="login-google-button"
      >
        {isSigningIn ? (
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        ) : (
          <GoogleIcon />
        )}
        {isSigningIn ? "Signing in..." : "Access with Google"}
      </Button>
    </div>
  );
};
